<template>
  <v-card class="event-counters-container pa-4">
    <div class="d-flex justify-space-between align-center mb-4">
      <h3 class="text-h6 text--secondary font-weight-bold">Summary</h3>
      <date-range-picker
        v-model="dateRange"
        :single-date-picker="false"
        :ranges="ranges"
        opens="left"
        :auto-apply="true"
        :min-date="new Date('03-10-2025')"
        :max-date="new Date()"
        @update="getCountersData"
        @clickRange="onRangeClick"
        control-container-class="table-date-range"
      >
        <template v-slot:input="">
          <div class="table-date-range">
            {{ dateRange.startDate.toLocaleDateString() }} -
            {{ dateRange.endDate.toLocaleDateString() }}
          </div>
        </template>
      </date-range-picker>
    </div>
    <v-row style="gap: 8px; padding: 8px">
      <div class="trend-card">
        <div class="event-counter-card">
          <div class="event-counter-title">New Users</div>
          <div class="event-counter-value">
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="primary"
              size="24"
              width="2"
            ></v-progress-circular>
            <template v-else>
              {{ eventsCount[EVENT_TYPES.SING_UP] }}
            </template>
          </div>
        </div>
      </div>
      <div class="trend-card">
        <div class="event-counter-card">
          <div class="event-counter-title">Workouts Completed</div>
          <div class="event-counter-value">
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="primary"
              size="24"
              width="2"
            ></v-progress-circular>
            <template v-else>
              {{ eventsCount[EVENT_TYPES.WORKOUT_COMPLETED] }}
            </template>
          </div>
        </div>
      </div>
      <div class="trend-card">
        <div class="event-counter-card">
          <div class="event-counter-title">Subscriptions Created</div>
          <div class="event-counter-value">
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="primary"
              size="24"
              width="2"
            ></v-progress-circular>
            <template v-else>
              {{ eventsCount[EVENT_TYPES.SUBSCRIPTION_CREATED] }}
            </template>
          </div>
        </div>
      </div>
      <div class="trend-card">
        <div class="event-counter-card">
          <div class="event-counter-title">Posts Created</div>
          <div class="event-counter-value">
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="primary"
              size="24"
              width="2"
            ></v-progress-circular>
            <template v-else>
              {{ eventsCount[EVENT_TYPES.POST_CREATED] }}
            </template>
          </div>
        </div>
      </div>
      <div class="trend-card">
        <div class="event-counter-card">
          <div class="event-counter-title">Active Users</div>
          <div class="event-counter-value">
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="primary"
              size="24"
              width="2"
            ></v-progress-circular>
            <template v-else>
              {{ activeUsers }}
            </template>
          </div>
        </div>
      </div>
    </v-row>
  </v-card>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import moment from "moment";
import { EVENT_TYPES } from "@/constants/eventTypes";

export default {
  name: "TrendsCard",
  components: {
    DateRangePicker,
  },
  data: () => ({
    dateRange: {
      startDate: moment().startOf("day").toDate(),
      endDate: moment().endOf("day").toDate(),
    },
    ranges: {},
    eventsCount: {},
    loading: false,
    EVENT_TYPES,
    activeUsers: 0,
  }),
  created() {
    const today = moment().clone();
    const yesterday = today.clone().subtract(1, "day");
    const last7Days = today.clone().subtract(7, "days");
    const last30Days = today.clone().subtract(30, "days");

    this.ranges = {
      Today: [today.startOf("day").toDate(), today.endOf("day").toDate()],
      Yesterday: [yesterday.startOf("day").toDate(), yesterday.endOf("day").toDate()],
      "Last 7 days": [last7Days.startOf("day").toDate(), today.endOf("day").toDate()],
      "Last 30 days": [last30Days.startOf("day").toDate(), today.endOf("day").toDate()],
    };

    this.getCountersData();
  },
  methods: {
    onRangeClick(dateRange) {
      // When a range is clicked, manually set the date range
      if (dateRange && dateRange.length === 2) {
        this.dateRange = {
          startDate: dateRange[0],
          endDate: dateRange[1],
        };
        this.getCountersData(this.dateRange);
      }
    },
    async getCountersData(newDates) {
      this.loading = true;
      try {
        const startDate = newDates
          ? moment(newDates.startDate).startOf("day").toDate()
          : moment(this.dateRange.startDate).startOf("day").toDate();
        const endDate = newDates
          ? moment(newDates.endDate).endOf("day").toDate()
          : moment(this.dateRange.endDate).endOf("day").toDate();
        const [trends, activeUsers] = await Promise.all([
          this.$store.dispatch("getTrends", {
            startDate,
            endDate,
            types: [
              EVENT_TYPES.SING_UP,
              EVENT_TYPES.WORKOUT_COMPLETED,
              EVENT_TYPES.SUBSCRIPTION_CREATED,
              EVENT_TYPES.POST_CREATED,
            ],
          }),
          await this.$store.dispatch("getActiveUsers", {
            startDate,
            endDate,
          }),
        ]);
        this.activeUsers = activeUsers;
        this.eventsCount = trends;
      } catch (error) {
        console.error("Error fetching counter events:", error);
        this.events = [];
      } finally {
        this.loading = false;
      }
    },
    getEventCount(eventType) {
      return this.events.filter((event) => event.eventType === eventType)
        .length;
    },
  },
};
</script>

<style scoped>
.table-date-range {
  font-weight: bold;
  font-size: 12px;
  color: #3a3a3a;
  text-align: center;
  border-width: 1px;
  border-style: solid;
  border-color: #3a3a3a;
  border-radius: 8px;
  padding: 0px 8px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table-date-range:hover {
  cursor: pointer;
}

.event-counters-container {
  height: 160px;
  width: 1000px;
}

.event-counter-card {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
  height: 100%;
}

.event-counter-title {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
}

.event-counter-value {
  color: #333;
  font-size: 24px;
  font-weight: bold;
}

.trend-card {
  flex: 1;
}
</style> 